import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Cluster } from '@googlemaps/markerclusterer/dist/cluster';
import Helper from '../../common/utils/helper';

type markerInfoType = {
    info: string
    latitude: string
    longitude: string
    name: string
    imageMarker: string
    picto: string
    shop_id: number
}

type customMarker = google.maps.Marker
    & { info: string }

type customMarkerOptions = google.maps.MarkerOptions
    & { info: string }

export default abstract class StoreLocatorBase {
    protected markers: any[];

    protected map: google.maps.Map | null;

    protected currentInfoWindow: google.maps.InfoWindow | null;

    protected markerCluster: any;

    constructor() {
        this.markers = [];
        this.map = null;
        this.currentInfoWindow = null;
        this.init();
    }

    init() {
        this.initMap();
    }

    initMap() {
        // On va mettre des coordonnées un peu bidon pour centrer la carte sur la région Bretagne
        const DEFAULT_LAT = 47.658072;
        const DEFAULT_LNG = -1.97702;
        const DEFAULT_ZOOM = 8;
        const mapElement = document.getElementById('map');
        const loader = new Loader({
            apiKey: mapElement ? (mapElement.getAttribute('data-key') ?? '') : '',
            version: 'weekly',
        });

        if (mapElement) {
            return loader.load().then(() => {
                this.map = new google.maps.Map(mapElement, {
                    zoom: DEFAULT_ZOOM,
                    center: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
                    streetViewControl: false,
                    mapTypeControl: false,
                });
                google.maps.event.trigger(this.map, 'resize');
                // Change map color
                this.changeMapStyle();
                // init markers
                this.initMarkers();
            });
        }
        return null;
    }

    abstract initMarkers(): void;

    changeMapStyle() {
        if (this.map) {
            this.map.setOptions({
                styles:
                    [
                        {
                            elementType: 'geometry',
                            stylers: [
                                {
                                    color: '#f5f5f5',
                                },
                            ],
                        },
                        {
                            elementType: 'labels.icon',
                            stylers: [
                                {
                                    visibility: 'off',
                                },
                            ],
                        },
                        {
                            elementType: 'labels.text.fill',
                            stylers: [
                                {
                                    color: '#616161',
                                },
                            ],
                        },
                        {
                            elementType: 'labels.text.stroke',
                            stylers: [
                                {
                                    color: '#f5f5f5',
                                },
                            ],
                        },
                        {
                            featureType: 'administrative.land_parcel',
                            elementType: 'labels.text.fill',
                            stylers: [
                                {
                                    color: '#bdbdbd',
                                },
                            ],
                        },
                        {
                            featureType: 'landscape',
                            elementType: 'geometry.fill',
                            stylers: [
                                {
                                    color: '#f7f7f7',
                                },
                            ],
                        },
                        {
                            featureType: 'landscape.natural.landcover',
                            elementType: 'geometry.fill',
                            stylers: [
                                {
                                    visibility: 'off',
                                },
                            ],
                        },
                        {
                            featureType: 'poi',
                            elementType: 'geometry',
                            stylers: [
                                {
                                    color: '#eeeeee',
                                },
                            ],
                        },
                        {
                            featureType: 'poi',
                            elementType: 'labels.text.fill',
                            stylers: [
                                {
                                    color: '#757575',
                                },
                            ],
                        },
                        {
                            featureType: 'poi.park',
                            elementType: 'geometry',
                            stylers: [
                                {
                                    color: '#e5e5e5',
                                },
                            ],
                        },
                        {
                            featureType: 'poi.park',
                            elementType: 'geometry.fill',
                            stylers: [
                                {
                                    color: '#e5e5e5',
                                },
                            ],
                        },
                        {
                            featureType: 'poi.park',
                            elementType: 'labels.text.fill',
                            stylers: [
                                {
                                    color: '#9e9e9e',
                                },
                            ],
                        },
                        {
                            featureType: 'road',
                            elementType: 'geometry',
                            stylers: [
                                {
                                    color: '#ffffff',
                                },
                            ],
                        },
                        {
                            featureType: 'road.arterial',
                            elementType: 'geometry.fill',
                            stylers: [
                                {
                                    color: '#ffffff',
                                },
                            ],
                        },
                        {
                            featureType: 'road.arterial',
                            elementType: 'geometry.stroke',
                            stylers: [
                                {
                                    color: '#ffffff',
                                },
                            ],
                        },
                        {
                            featureType: 'road.arterial',
                            elementType: 'labels.text.fill',
                            stylers: [
                                {
                                    color: '#757575',
                                },
                            ],
                        },
                        {
                            featureType: 'road.highway',
                            elementType: 'geometry',
                            stylers: [
                                {
                                    color: '#dadada',
                                },
                            ],
                        },
                        {
                            featureType: 'road.highway',
                            elementType: 'geometry.fill',
                            stylers: [
                                {
                                    color: '#ffffff',
                                },
                            ],
                        },
                        {
                            featureType: 'road.highway',
                            elementType: 'geometry.stroke',
                            stylers: [
                                {
                                    color: '#ffffff',
                                },
                            ],
                        },
                        {
                            featureType: 'road.highway',
                            elementType: 'labels.text.fill',
                            stylers: [
                                {
                                    color: '#616161',
                                },
                            ],
                        },
                        {
                            featureType: 'road.local',
                            elementType: 'geometry.fill',
                            stylers: [
                                {
                                    color: '#ffffff',
                                },
                            ],
                        },
                        {
                            featureType: 'road.local',
                            elementType: 'geometry.stroke',
                            stylers: [
                                {
                                    color: '#ffffff',
                                },
                            ],
                        },
                        {
                            featureType: 'road.local',
                            elementType: 'labels.text.fill',
                            stylers: [
                                {
                                    color: '#9e9e9e',
                                },
                            ],
                        },
                        {
                            featureType: 'transit.line',
                            elementType: 'geometry',
                            stylers: [
                                {
                                    color: '#e5e5e5',
                                },
                            ],
                        },
                        {
                            featureType: 'transit.station',
                            elementType: 'geometry',
                            stylers: [
                                {
                                    color: '#eeeeee',
                                },
                            ],
                        },
                        {
                            featureType: 'water',
                            elementType: 'geometry',
                            stylers: [
                                {
                                    color: '#c9c9c9',
                                },
                            ],
                        },
                        {
                            featureType: 'water',
                            elementType: 'geometry.fill',
                            stylers: [
                                {
                                    color: '#ededed',
                                },
                            ],
                        },
                        {
                            featureType: 'water',
                            elementType: 'labels.text.fill',
                            stylers: [
                                {
                                    color: '#9e9e9e',
                                },
                            ],
                        },
                    ],
            });
        }
    }

    deleteMarkers(): void {
        for (let i = 0; i < this.markers.length; i += 1) {
            this.markers[i].setMap();
        }
        this.markers = [];
        if (this.markerCluster) {
            this.markerCluster.clearMarkers();
        }
    }

    addMarkers(markerList: string) {
        const obj = JSON.parse(markerList);
        const markerDefaultColor = getComputedStyle(document.documentElement).getPropertyValue('--tertiary') || getComputedStyle(document.documentElement).getPropertyValue('--bs-primary');
        obj.forEach((markerInfo: markerInfoType) => {
            if (markerInfo !== null && markerInfo.latitude !== undefined && markerInfo.longitude !== undefined) {
                const markerPosition = new google.maps.LatLng(
                    parseFloat(markerInfo.latitude),
                    parseFloat(markerInfo.longitude),
                );

                const options: customMarkerOptions = {
                    map: this.map,
                    position: markerPosition,
                    title: markerInfo.name,
                    info: markerInfo.info,
                };

                // picto
                if (markerInfo.imageMarker != null && markerInfo.imageMarker !== '') {
                    options.icon = {
                        url: markerInfo.imageMarker,
                        scaledSize: new google.maps.Size(45, 45),
                    };
                } else {
                    options.icon = {
                        path: markerInfo.picto,
                        fillColor: markerDefaultColor,
                        fillOpacity: 1,
                        strokeWeight: 0,
                        scale: 1,
                        scaledSize: new google.maps.Size(28, 40),
                    };
                }

                const marker = new google.maps.Marker(options) as customMarker;

                this.initInfoWindow(marker);
                this.markers.push(marker);
            }
        });
        if (this.markerCluster) {
            this.markerCluster.addMarkers(this.markers);
        } else {
            const renderer = {
                render: ({ count, position }: Cluster) => new google.maps.Marker({
                    label: { text: String(count), color: 'white', fontSize: '10px' },
                    position,
                    // adjust zIndex to be above other markers
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                }),
            };
            this.markerCluster = new MarkerClusterer({ map: this.map, markers: this.markers, renderer });
        }
        this.boundMap();
    }

    closeCurrentInfoWindow() {
        if (this.currentInfoWindow !== null) {
            this.currentInfoWindow.close();
        }
    }

    initInfoWindow(marker: customMarker) {
        const InfoWindow = new google.maps.InfoWindow({
            content: '',
        });
        marker.addListener('click', () => {
            this.closeCurrentInfoWindow();
            InfoWindow.open(this.map, marker);
            InfoWindow.setContent('Loading...');
            fetch(marker.info, {
                method: 'POST',
                headers: Helper.createFetchHeader(),
            }).then((response) => Promise.all([response, response.text()]))
                .then(([, data]) => {
                    InfoWindow.setContent(data);
                    this.currentInfoWindow = InfoWindow;
                });
        });
    }

    /**
     * On centre et zoom sur les points présents dans la map
     */
    boundMap() {
        // création d'un objet de contour LatLngBounds
        const bounds = new google.maps.LatLngBounds();
        for (let i = 0; i < this.markers.length; i += 1) {
            // On ajoute les position pour que l'objet ai toutes les coordonnées
            bounds.extend(this.markers[i].position);
        }

        if (this.markers.length > 1) {
            // on resize la map au bord de cet objet
            this.map?.fitBounds(bounds);
        } else if (this.markers.length === 1) {
            this.map?.setCenter(this.markers[0].position);
            // Si on n'a qu'un point on recule le zoom car si un seul point le zoom auto est horrible
            this.map?.setZoom(9);
        }
    }
}
